import usePageTitle from "../../core/hooks/UsePageTitle";
import DefaultLayout from "../layouts/default/Default.layout";
import { Col, Form, Input, Row, Button, Modal, notification } from "antd";
import { EditOutlined, DeleteOutlined } from "@ant-design/icons";
import { Banca } from "../../sdk/@types";
import BancaService from "../../sdk/services/BancaService";
import useBancas from "../../core/hooks/UseBanca";
import useUsuario from "../../core/hooks/UseUsuario";
import { Column, useTable } from "react-table";
import { useMemo, useState, useEffect } from "react";
import Table from "../components/Table/Table";

export default function BancaForm() {
  const [form] = Form.useForm<Banca.Banca>();
  const [editingId, setEditingId] = useState<string | null>(null);
  usePageTitle("Banca");
  const { bancas, fetchBancas } = useBancas();
  const { usuario } = useUsuario();
  const isMaster = usuario?.profile === "master";

  useEffect(() => { fetchBancas(); }, []);

  const handleEdit = (banca: Banca.Banca) => {
    setEditingId(banca.id ?? null);
    form.setFieldsValue({ year: banca.year, name: banca.name });
  };

  const handleDelete = (banca: Banca.Banca) => {
    Modal.confirm({
      title: "Excluir banca",
      content: `Deseja realmente excluir "${banca.name}"?`,
      okText: "Excluir",
      okType: "danger",
      cancelText: "Cancelar",
      centered: true,
      onOk: async () => {
        try {
          await BancaService.deletarBanca(banca.id!);
          notification.success({ message: "Banca excluída com sucesso." });
          fetchBancas();
        } catch {
          notification.error({ message: "Erro ao excluir banca." });
        }
      },
    });
  };

  const columns: Column<Banca.Banca>[] = useMemo(() => [
    { Header: "Ano",  accessor: "year" },
    { Header: "Nome", accessor: "name" },
    {
      Header: "Ações",
      id: "acoes",
      Cell: ({ row }: any) => isMaster ? (
        <div className="flex gap-2">
          <Button
            size="small"
            icon={<EditOutlined />}
            onClick={() => handleEdit(row.original)}
          >
            Editar
          </Button>
          <Button
            size="small"
            danger
            icon={<DeleteOutlined />}
            onClick={() => handleDelete(row.original)}
          >
            Excluir
          </Button>
        </div>
      ) : null,
    },
  ], [bancas, isMaster]);

  const instance = useTable<Banca.Banca>({ columns, data: bancas });

  const handleCancel = () => {
    setEditingId(null);
    form.resetFields();
  };

  return (
    <DefaultLayout>
      <div className="p-4">
        <Form
          form={form}
          onFinish={async (values: Banca.Banca) => {
            try {
              if (editingId) {
                await BancaService.atualizarBanca({ ...values, id: editingId });
                notification.success({ message: "Banca atualizada com sucesso." });
              } else {
                await BancaService.inserirNovaBanca(values);
                notification.success({ message: "Banca cadastrada com sucesso." });
              }
              fetchBancas();
              form.resetFields();
              setEditingId(null);
            } catch {
              notification.error({ message: "Houve um erro." });
            }
          }}
          layout="vertical"
          autoComplete="off"
        >
          <Row gutter={16}>
            <Col span={5}>
              <Form.Item label="Ano" name="year">
                <Input placeholder="Ex.: 2022" maxLength={4} />
              </Form.Item>
            </Col>
            <Col span={15}>
              <Form.Item label="Nome da Banca" name="name">
                <Input placeholder="Ex.: CBM-GO" maxLength={60} />
              </Form.Item>
            </Col>
            <Col span={24} className="flex gap-2">
              <Button type="primary" htmlType="submit">
                {editingId ? "Atualizar" : "Salvar"}
              </Button>
              {editingId && (
                <Button onClick={handleCancel}>Cancelar</Button>
              )}
            </Col>
            <Col span={24} style={{ marginTop: 20 }}>
              <Table instance={instance} />
            </Col>
          </Row>
        </Form>
      </div>
    </DefaultLayout>
  );
}
